/**
 * 路由元数据 Hook - 在组件中使用路由元数据
 * 使用方法：
 * import { useRouteMetadata } from '@/hooks/useRouteMetadata';
 * const { cnName, name, meta } = useRouteMetadata();
 */

import {
  getRouteCnName,
  getRouteComponent,
  getRouteMetadata,
  getRouteName,
  getRouteSeoMeta,
  isRoute,
} from '@/utils/route-metadata';
import { usePathname } from 'next/navigation';

/**
 * 路由元数据 Hook
 * @returns 当前路由的元数据
 */
export function useRouteMetadata() {
  const pathname = usePathname();

  const metadata = getRouteMetadata(pathname);

  return {
    // 基础信息
    pathname,
    name: metadata?.name || 'Unknown',
    cnName: metadata?.cnName || '未知页面',
    component: metadata?.component || 'UnknownComponent',

    // 元数据
    meta: metadata?.meta || {},

    // 便捷方法
    getCnName: () => getRouteCnName(pathname),
    getName: () => getRouteName(pathname),
    getComponent: () => getRouteComponent(pathname),
    getSeoMeta: () => getRouteSeoMeta(pathname),

    // 路由检查
    isHome: () => isRoute(pathname, 'Home'),
    isGoodsList: () => isRoute(pathname, 'GoodsList'),
    isGoodsDetail: () => isRoute(pathname, 'GoodsDetail'),
    isCart: () => isRoute(pathname, 'ShoppingCart'),
    isAccount: () => isRoute(pathname, 'Account'),
    isBlog: () => isRoute(pathname, 'Blog'),
    isFitGuide: () => isRoute(pathname, 'FitGuide'),
    isShippingTracking: () => isRoute(pathname, 'ShippingTracking'),
    isGoodsListPreview: () => isRoute(pathname, 'GoodsListPreview'),
    isNotFound: () => isRoute(pathname, 'NotFound'),

    // 通用检查
    isRoute: (routeName: string) => isRoute(pathname, routeName),
  };
}

/**
 * 路由中文名称 Hook
 * @returns 当前路由的中文名称
 */
export function useRouteCnName(): string {
  const pathname = usePathname();
  return getRouteCnName(pathname);
}

/**
 * 路由英文名称 Hook
 * @returns 当前路由的英文名称
 */
export function useRouteName(): string {
  const pathname = usePathname();
  return getRouteName(pathname);
}

/**
 * 路由SEO元数据 Hook
 * @returns 当前路由的SEO元数据
 */
export function useRouteSeoMeta() {
  const pathname = usePathname();
  return getRouteSeoMeta(pathname);
}

/**
 * 路由检查 Hook
 * @param routeName 路由名称
 * @returns 是否为指定路由
 */
export function useIsRoute(routeName: string): boolean {
  const pathname = usePathname();
  return isRoute(pathname, routeName);
}
